/* eslint-disable complexity */
import React, { useMemo, useRef, useState } from 'react';
import Badge from 'src/@vymo/ui/atoms/badge';
import Button from 'src/@vymo/ui/atoms/button';
import Text from 'src/@vymo/ui/atoms/text';
import { NoData } from 'src/@vymo/ui/blocks';
import Form from 'src/@vymo/ui/molecules/form';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import Page from 'src/@vymo/ui/molecules/page';
import classnames from 'classnames';
import { ReactComponent as Cross } from 'src/assets/icons/close.svg';
import { ReactComponent as FilterIcon } from 'src/assets/icons/filter.svg';
import { Keys, locale } from 'src/i18n';
import { isDesktop } from 'src/workspace/utils';
import { FiltersProps } from './types';
import styles from './index.module.scss';

function Filters({
  filterFields,
  metaData,
  formVersion = FormVersion.webRevyre,
  filters = {},
  filterCount = 0,
  onApply,
  onReset,
  title,
  applyButtonText,
  resetButtonText,
  showBadge = true,
  className,
  buttonClassname,
}: FiltersProps) {
  const formRef = useRef(null);
  const [show, setShow] = useState(false);

  const panelClasses = classnames(
    styles.filters__panel,
    show && styles.filters__panel__open,
    className,
  );

  const buttonClasses = classnames(
    styles.filters__button,
    show && styles.filters__button__open,
    buttonClassname,
  );

  const handleFilterButtonClick = () => {
    setShow(!show);
  };

  const onSubmitHandler = async () => {
    // @ts-ignore
    const result = await formRef.current?.getFieldsForSubmission?.();
    if (result?.valid === 'valid') {
      const payload: Record<string, any> = {};
      const filterValuesData = result?.data;

      filterValuesData?.forEach((field) => {
        if (field?.value != null && field?.value !== '') {
          payload[field?.code] = field?.data?.code ?? field?.value;
        }
      });

      setShow(false);

      // Call onApply callback - module layer will handle Redux actions
      if (onApply) {
        onApply(payload);
      }
    }
  };

  const resetHandler = () => {
    setShow(false);

    // Call onReset callback - module layer will handle Redux actions
    if (onReset) {
      onReset();
    }
  };

  const filterValueMap = useMemo(() => {
    const map = {};
    Object.keys(filters || {}).forEach((code) => {
      map[code] = {
        code,
        value: filters?.[code],
      };
    });
    return map;
  }, [filters]);

  const formRenderKey = useMemo(
    () => JSON.stringify(filterValueMap),
    [filterValueMap],
  );

  // Don't render if no filter fields are available
  if (!filterFields?.length) {
    return null;
  }

  const getBadgeComponent = () => (
    <div className={buttonClasses}>
      {showBadge && (
        <Badge
          content={filterCount > 0 ? filterCount : null}
          variant="subtle"
          type="dot"
        >
          <Button
            type="primary"
            className={styles.filters__button_badge}
            variant="subtle"
            size="large"
            data-test-id="filter-button"
            onClick={handleFilterButtonClick}
            iconProps={{ icon: <FilterIcon />, iconPosition: 'left' }}
          />
        </Badge>
      )}
      {!showBadge && (
        <Button
          type={filterCount > 0 ? 'primary' : 'outlined'}
          className={styles.filters__button_badge}
          variant="subtle"
          size="large"
          data-test-id="filter-button"
          onClick={handleFilterButtonClick}
          iconProps={{ icon: <FilterIcon />, iconPosition: 'left' }}
        />
      )}
    </div>
  );

  return (
    <div className={styles.filters}>
      {getBadgeComponent()}
      <Page className={panelClasses}>
        <Page.Header>
          <div className={styles.filters__header}>
            <Text data-test-id="filters-title" type="h5">
              {title || locale(Keys.ALL_FILTERS)}
            </Text>
            <Button
              size="small"
              type="text"
              iconProps={{ icon: <Cross />, iconPosition: 'right' }}
              className={styles.modal__closeButton}
              onClick={() => setShow(false)}
            />
          </div>
        </Page.Header>
        <Page.Body>
          <div className={styles.filters__panel__content}>
            {filterFields?.length ? (
              <Form
                data-test-id="filter-form"
                key={`${filterFields.length}-${formRenderKey}`}
                ref={formRef}
                id="filterForm"
                name="filterForm"
                span={isDesktop() ? '1fr' : '1fr'}
                onChange={() => {}}
                config={{
                  version: formVersion,
                  data: filterFields || [],
                  grouping: [],
                  fieldItemConfig: {
                    showDisabledIcon: true,
                  },
                  metaData: metaData || {},
                }}
                value={filterValueMap}
                className={styles.filters__form}
              />
            ) : (
              <NoData message={locale(Keys.NO_FILTER_FIELDS_FOUNDS)} />
            )}
          </div>
        </Page.Body>
        <Page.Footer position="left">
          <Button
            type="primary"
            data-test-id="apply-filter-button"
            onClick={onSubmitHandler}
            disabled={!filterFields?.length}
          >
            {applyButtonText || locale(Keys.APPLY_FILTERS)}
          </Button>
          <Button
            type="primary"
            data-test-id="reset-filter-button"
            onClick={resetHandler}
            disabled={!filterFields?.length}
          >
            {resetButtonText || locale(Keys.RESET)}
          </Button>
        </Page.Footer>
      </Page>
    </div>
  );
}

export default Filters;
